// App.js
import React, { useEffect } from "react";
import DepartmentPage from "./pages/DepartmentPage";
import SignUp from "./pages/SignUp";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import Onboarding from "./pages/OnboardingPage";
import Profile from "./pages/ProfilePage";
import UserManagementPage from "./pages/UserManagementPage";
import Test from "./pages/Test";
import Background from "./components/Background";
import SettingsPage from "./pages/SettingsPage";
import UserProfile from "./pages/UserProfile";
import AI from "./pages/AI";
import Analytics from "./pages/Analytics";
import ContactUs from "./pages/ContactUs";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePassword from "./pages/ChangePassword";
import FilesInsideFolder from "./components/FilesInsideFolder";
// import secureLocalStorage from "react-secure-storage";
import CheckoutPage from "./pages/CheckoutPage";
import StandaloneFileViewer from "./pages/StandaloneFileViewer";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import PlanGate from "./components/PlanGate";
import ErrorPage from "./components/ErrorPage";
import TextEditor from "./components/FileEditors/TextEditor";
import CustomEditor from "./components/customEditor/CustomEditorV1";
import WaitingLobby from "./components/WaitingLobby";
import LandingPrev from "./pages/LandingPrev";
// import Landing from "./pages/Landing";
import AuthHandler from "./pages/AuthHandler";
import CreateOrganization from "./pages/CreateOrganization";
import Chat from "./pages/Chat";
import RecycleBin from "./pages/RecycleBin";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import useAuth from "./stores/authStore";
import { auth } from "./helper/firebaseClient";
import PrivacyPolicy from "./pages/PrivacyPolicy";
// import { useDarkMode } from "./context/darkModeContext";
// import TestPricing from "./components/TestPricing";
import SeatUpgrade from "./components/SeatUpgrade";
import HomePricing from "./components/landingPage/HomePricing";
import MailCallback from "./pages/MailCallback";
import TasksComingSoon from "./pages/TasksComingSoon";
import TaskAndTickets from "./pages/TicketsBoard";
import ClientDeliveryRooms from "./pages/ClientDeliveryRooms";
import VendorRoomDetail from "./pages/VendorRoomDetail";
import ClientDeliverablePage from "./pages/ClientDeliverablePage";

// let hardCodedDepartments = [
//   { name: "Account", metadata: { bg: "#FFF6F6", border: "#FEB7B7" } },
//   { name: "Finance", metadata: { bg: "#FFF6FF", border: "#FFA9FF" } },
//   { name: "Development", metadata: { bg: "#F6FFF6", border: "#B3FFB3" } },
//   { name: "Manufacturing", metadata: { bg: "#F6F7FF", border: "#B6BEFF" } },
//   { name: "Sales", metadata: { bg: "#FFFFF6", border: "#FFFFA1" } },
//   { name: "Human Resources", metadata: { bg: "#F6FFFE", border: "#C0FFF8" } },
// ];

// const REFRESH_INTERVAL = 24 * 60 * 60 * 1000;

// let fetchedDepartments = JSON.parse(secureLocalStorage.getItem("departments"));

const queryClient = new QueryClient();

// Wraps regular dashboard/workspace pages with the internal app chrome
// (sidebar, topbar, background). Standalone/external-facing routes (e.g.
// the client-facing deliverable portal) are rendered outside of this
// layout so they mount without any internal navigation chrome.
const InternalLayout = () => {
  return (
    <div className="md:flex ">
      <SideBar />
      <div className="w-full">
        <TopBar />
        <Background />
        <Outlet />
      </div>
    </div>
  );
};

const App = () => {
  // const { refreshAccessToken } = useAuth();
  // const isDarkMode = useDarkMode();

  // TODO: implement refresh token logic

  // useEffect(() => {
  //   const intervalId = setInterval(() => {
  //     refreshAccessToken();
  //   }, REFRESH_INTERVAL);
  //   return () => clearInterval(intervalId);
  // }, [refreshAccessToken]);

  useEffect(() => {
    // document.addEventListener("keydown", detectPrintScreen);
    document.addEventListener(
      "contextmenu",
      useAuth.getState().handleContextMenu,
    );
    return () => {
      // document.removeEventListener("keydown", detectPrintScreen);
      document.removeEventListener(
        "contextmenu",
        useAuth.getState().handleContextMenu,
      );
    };
  }, []);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const token = await user.getIdToken();

        useAuth.setState({ token });

        await useAuth.getState().fetchProfileData();
      } else {
        useAuth.setState({
          token: null,
          profileData: null,
          profileIsPending: false,
        });
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Mobile Safari / Chrome (iPad, phones) keep tabs suspended in memory and
  // restore them WITHOUT reloading the page (bfcache / tab resume). In that
  // case onAuthStateChanged above never fires again, so the tab keeps showing
  // whatever profile it had when it was last open — e.g. "subscription
  // expired → /checkout" even though the customer has since renewed from
  // another device. Re-fetch the profile silently whenever the tab comes back.
  useEffect(() => {
    let lastRun = 0;

    const refreshProfile = () => {
      if (!auth.currentUser) return;
      const now = Date.now();
      if (now - lastRun < 5000) return; // throttle rapid focus/visibility flips
      lastRun = now;
      useAuth.getState().fetchProfileData({ silent: true });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshProfile();
    };

    const onPageShow = (event) => {
      // persisted === true means the page was restored from the bfcache
      if (event.persisted) refreshProfile();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // ── Subscription expiry ────────────────────────────────────────────────────
  // 1) The API answers 403 {code: "subscription_expired"} once the org has
  //    lapsed (see utils/axios-instance.js). Re-fetch the profile so
  //    ProtectedRoute can redirect the admin to /checkout straight away.
  useEffect(() => {
    let lastRun = 0;
    const onSubscriptionExpired = () => {
      if (!auth.currentUser) return;
      const now = Date.now();
      if (now - lastRun < 5000) return; // many parallel calls fail together
      lastRun = now;
      useAuth.getState().fetchProfileData({ silent: true });
    };
    window.addEventListener("subscription-expired", onSubscriptionExpired);
    return () =>
      window.removeEventListener("subscription-expired", onSubscriptionExpired);
  }, []);

  // 2) A session that is already open when the paid period ends: re-check with
  //    the server at expiry time (the server decides; the client clock is only
  //    used to pick WHEN to ask, so a wrong device clock cannot lock anyone out).
  const subscriptionExpiresAt = useAuth(
    (s) => s.profileData?.subscription_expires_at,
  );
  const subscriptionActive = useAuth((s) => s.profileData?.subscription_active);
  const profileFetchedAt = useAuth((s) => s.profileFetchedAt);

  useEffect(() => {
    if (!subscriptionExpiresAt || subscriptionActive === false)
      return undefined;
    const expiry = new Date(subscriptionExpiresAt).getTime();
    if (Number.isNaN(expiry)) return undefined;
    const msLeft = expiry - Date.now();
    if (msLeft <= 0) return undefined;

    // setTimeout caps out near 24 days, so wake up at least every 30 minutes.
    const timer = setTimeout(
      () => {
        if (auth.currentUser) {
          useAuth.getState().fetchProfileData({ silent: true });
        }
      },
      Math.min(msLeft + 2000, 30 * 60 * 1000),
    );
    return () => clearTimeout(timer);
  }, [subscriptionExpiresAt, subscriptionActive, profileFetchedAt]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Standalone client-facing portal: no internal nav chrome */}
          <Route
            path="/vendors/room/:roomId/deliverable/:deliverableId"
            element={<ClientDeliverablePage />}
          />

          <Route element={<InternalLayout />}>
            {/* <Route path="/test-pricing" element={<TestPricing />} /> */}
            <Route path="/home-pricing" element={<HomePricing />} />
            <Route path="/" element={<LandingPrev />} />
            {/* <Route path="/" element={<Landing />} /> */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/signup" element={<SignUp />} />
            <Route
              path="/create-organization"
              element={<CreateOrganization />}
            />
            <Route path="/login" element={<LoginPage />} exact />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/_/auth_handler" element={<AuthHandler />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            {/* Obsolete under the Google Identity Services popup code flow,
                which has no redirect leg — nothing navigates here any more.
                Kept so a stale bookmark or an old email link lands on the
                redirect stub rather than a 404. Safe to delete this route and
                the component once nothing external points at it. */}
            <Route path="/auth/:provider/callback" element={<MailCallback />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/file/:fileId" element={<StandaloneFileViewer />} />
              <Route path="/chat" element={<Chat />} />
              <Route
                path="/user-management"
                element={
                  <PlanGate>
                    <UserManagementPage />
                  </PlanGate>
                }
              />
              <Route
                path="/department/:deptName"
                element={<DepartmentPage />}
              />
              <Route path="/user-profile/:userId" element={<UserProfile />} />
              <Route
                path="/filesInsideFolder/:folderName/:folderId"
                element={<FilesInsideFolder />}
              />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/waiting-lobby" element={<WaitingLobby />} />

              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai/:fileId" element={<AI />} />

              {/* Development-only routes.
                  /test, /edit and /editcustom are scratch pages for editor
                  work. They were reachable in production, which gives anyone
                  unreviewed surface to poke at and reads to a reviewer as an
                  uncurated route table. Gated on the build environment rather
                  than deleted, so local work is unaffected. */}
              <Route path="/test" element={<Test />} />

              {/* for editor testing */}
              <Route path="/edit" element={<TextEditor />} />
              <Route path="/editcustom" element={<CustomEditor />} />

              {/* for Recycle Bin */}
              <Route path="/recyclebin" element={<RecycleBin />} />

              {/* Internal pages. These used to sit OUTSIDE <ProtectedRoute>, so
                  an expired / unpaid org could still open them. Behind the
                  guard, an expired org admin is redirected to /checkout and
                  other members get the "subscription expired" notice. */}
              <Route path="/upgrade-seats" element={<SeatUpgrade />} />
              <Route path="/tasks-coming-soon" element={<TasksComingSoon />} />
              <Route path="/tasks-and-tickets" element={<TaskAndTickets />} />
              <Route path="/vendors" element={<ClientDeliveryRooms />} />
              <Route
                path="/vendors/room/:roomId"
                element={<VendorRoomDetail />}
              />
            </Route>
            <Route
              path="*"
              element={
                <ErrorPage error={"We could not find the requested page."} />
              }
            />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
